import type { MutableRefObject } from 'react';
import React, { useCallback, useMemo } from 'react';
import type { TooltipProps } from 'recharts';
import { useTheme } from '@mui/styles';
import type { ChartReportPart, ReportDateTimeframe, ReportProps } from 'c-reports/Types';
import { ReportType } from 'c-reports/Types';
import type { TranslationPath } from 'c-translation';
import { useCommonTranslation } from 'c-translation';
import CustomTooltip from 'c-main/Components/Campaign/CampaignReports/Views/ByField/CustomTooltip';
import { FieldReportType } from 'c-main/Components/Campaign/CampaignReports/types';
import type { PCAReportCountField, PCAReportField } from 'c-sdk';
import { EsPCAReportField, PCAReportMetric } from 'c-sdk';
import {
    GroupByFieldWidgetReport,
    MapWidgetReport,
    StaticChartWidgetReport,
} from './summaryWidgetReports.main';
import type { SummaryWidgetConfig, SummaryWidgetMetric } from './summaryCustomizationConfig.main';
import {
    SUMMARY_WIDGET_GROUP_BY_PCA_FIELD_MAP,
    SUMMARY_WIDGET_GROUP_BY_TRANSLATION_KEYS,
} from './summaryCustomizationConfig.main';
import { buildStandardSummaryExportData } from './campaignAnalyticsUtils';

type SummaryWidgetDefinition = {
    id: string;
    report: ReportProps<any, any>;
};

type VisibleSummaryWidgetDefinition = SummaryWidgetDefinition & {
    title: string;
    isHeatmap: boolean;
};

type MetricChartLabels = {
    actualLabel: string;
    actualLabelOverride?: string;
    expectedLabel: string;
    percentageLabel: string;
    missingLabel: string;
};

type Params = {
    id: number;
    source: string;
    environmentMode: string | null | undefined;
    fieldFilters: Record<string, string[]>;
    isDsp: boolean;
    isAverageAudienceMode: boolean;
    isGeopathOnly: boolean;
    selectedAudienceLabelForCharts: string | null;
    showDefaultDspExpectedLine: (widget: SummaryWidgetConfig) => boolean;
    showDefaultDspExpectedBar: (widget: SummaryWidgetConfig) => boolean;
    showDefaultDspTargetPercent: (widget: SummaryWidgetConfig) => boolean;
    actualDeliveredLabelImpacts: string;
    shareOfTotalLabel: string;
    percentLabelDecimals: number;
    actualLabelImpacts: string;
    actualLabelPlays: string;
    expectedLabelForImpactsCharts: string;
    expectedLabelImpacts: string;
    expectedLabelPlays: string;
    percentageLabelImpacts: string;
    percentageLabelPlays: string;
    inScheduleLabel: string;
    outScheduleLabel: string;
    unbookedLabel: string;
    timeframe: ReportDateTimeframe | null;
    countFields: PCAReportCountField[];
    metricTitles: Record<SummaryWidgetMetric, string>;
    summaryTitleBy: string;
    summaryTitleHeatmap: string;
    summaryTitleJoin: string;
    summaryTitleRunningTotal: string;
    dateLabelLabel: string;
    visibleWidgetConfigs: SummaryWidgetConfig[];
    initialStartDate: Date;
    initialEndDate: Date;
    summaryLoadingState: any;
    latestSummaryWindow: MutableRefObject<{
        start: Date;
        end: Date;
        timeframe: ReportDateTimeframe;
    } | null>;
    getWidgetData: (widget: SummaryWidgetConfig) => any;
    getMetricChartLabels: (metric: SummaryWidgetMetric) => MetricChartLabels;
    fetchExtraWidgetData: (
        widget: SummaryWidgetConfig,
        start: Date,
        end: Date,
        timeframe: ReportDateTimeframe,
    ) => Promise<any[]>;
    /** UL-8133 -- changes when a vistar_us2 target series loads, so fetched widgets redraw. */
    targetSeriesKey?: string;
    buildSummaryExportData: (
        rows: any[] | null | undefined,
        metric: SummaryWidgetMetric,
        xAxisLabels: string[],
    ) => any[] | undefined;
};

export const useCampaignAnalyticsSummaryWidgets = ({
    id,
    source,
    environmentMode,
    fieldFilters,
    isDsp,
    isAverageAudienceMode,
    isGeopathOnly,
    selectedAudienceLabelForCharts,
    showDefaultDspExpectedLine,
    showDefaultDspExpectedBar,
    showDefaultDspTargetPercent,
    actualDeliveredLabelImpacts,
    shareOfTotalLabel,
    percentLabelDecimals,
    actualLabelImpacts,
    actualLabelPlays,
    expectedLabelForImpactsCharts,
    expectedLabelImpacts,
    expectedLabelPlays,
    percentageLabelImpacts,
    percentageLabelPlays,
    inScheduleLabel,
    outScheduleLabel,
    unbookedLabel,
    timeframe,
    countFields,
    metricTitles,
    summaryTitleBy,
    summaryTitleHeatmap,
    summaryTitleJoin,
    summaryTitleRunningTotal,
    dateLabelLabel,
    visibleWidgetConfigs,
    initialStartDate,
    initialEndDate,
    summaryLoadingState,
    latestSummaryWindow,
    getWidgetData,
    getMetricChartLabels,
    fetchExtraWidgetData,
    buildSummaryExportData,
    targetSeriesKey = 'none',
}: Params) => {
    const theme = useTheme();
    const t = useCommonTranslation();

    const generateParts = useCallback(
        ({
            actual,
            expected,
            actualLabelOverride,
        }: {
            actual: string;
            expected?: string;
            actualLabelOverride?: string;
        }) => {
            if (isDsp) {
                // Geopath, audited and averages modes all treat the comparison audience as
                // the primary series, with total DSP impressions as the grey baseline.
                if (
                    (isGeopathOnly ||
                        isAverageAudienceMode ||
                        selectedAudienceLabelForCharts != null) &&
                    expected
                ) {
                    return [
                        {
                            dataKey: expected,
                            filterRole: 'delivered',
                            comparisonSeries: false,
                            lineProps: {
                                stroke: theme.palette.primary.main,
                            },
                            barProps: { fill: theme.palette.primary.main },
                        },
                        {
                            dataKey: actual,
                            labelOverride: actualLabelOverride,
                            filterRole: 'expected',
                            comparisonSeries: true,
                            lineProps: {
                                stroke: theme.palette.grey['500'],
                                strokeDasharray: '5 5',
                            },
                            barProps: { fill: theme.palette.grey['500'] },
                        },
                    ] as ChartReportPart[];
                }

                return [
                    {
                        dataKey: actual,
                        labelOverride: actualLabelOverride,
                        filterRole: 'delivered',
                        lineProps: { stroke: theme.palette.primary.main },
                        barProps: { fill: theme.palette.primary.main },
                    },
                ] as ChartReportPart[];
            }

            return [
                {
                    dataKey: expected,
                    filterRole: 'expected',
                    lineProps: {
                        stroke: theme.palette.grey['500'],
                        strokeDasharray: '5 5',
                    },
                    barProps: { fill: theme.palette.grey['500'] },
                },
                {
                    dataKey: actual,
                    filterRole: 'delivered',
                    lineProps: { stroke: theme.palette.primary.main },
                    barProps: { fill: theme.palette.primary.main },
                },
            ] as ChartReportPart[];
        },
        [
            isAverageAudienceMode,
            isDsp,
            isGeopathOnly,
            selectedAudienceLabelForCharts,
            theme.palette.grey,
            theme.palette.primary.main,
        ],
    );

    const dailyImpactParts = useMemo<ChartReportPart[]>(
        () =>
            generateParts({
                actual: actualLabelImpacts,
                expected: expectedLabelForImpactsCharts,
                actualLabelOverride: isDsp ? actualDeliveredLabelImpacts : undefined,
            }),
        [
            actualDeliveredLabelImpacts,
            actualLabelImpacts,
            expectedLabelForImpactsCharts,
            generateParts,
            isDsp,
        ],
    );

    const dailyPlayParts = useMemo<ChartReportPart[]>(
        () => generateParts({ actual: actualLabelPlays, expected: expectedLabelPlays }),
        [actualLabelPlays, expectedLabelPlays, generateParts],
    );

    const impactsTooltipLabel = useCallback(
        (
            reportType: FieldReportType,
            field: PCAReportField,
            expectedLabelOverride: string = expectedLabelForImpactsCharts,
        ) =>
            function ImpactsTooltipLabel(tooltipProps: TooltipProps<any, any>) {
                return (
                    <CustomTooltip
                        tooltipProps={tooltipProps}
                        expectedLabel={expectedLabelOverride}
                        percentageLabel={percentageLabelImpacts}
                        inScheduleLabel={inScheduleLabel}
                        outScheduleLabel={outScheduleLabel}
                        unbookedLabel={unbookedLabel}
                        timeFrame={timeframe}
                        type={reportType}
                        field={field}
                        metric={PCAReportMetric.Impacts}
                        countFields={isDsp ? [] : countFields}
                        hidePercentage={isDsp}
                    />
                );
            },
        [
            countFields,
            expectedLabelForImpactsCharts,
            inScheduleLabel,
            isDsp,
            outScheduleLabel,
            percentageLabelImpacts,
            timeframe,
            unbookedLabel,
        ],
    );

    const playsTooltipLabel = useCallback(
        (reportType: FieldReportType, field: PCAReportField) =>
            function PlaysTooltipLabel(tooltipProps: TooltipProps<any, any>) {
                return (
                    <CustomTooltip
                        tooltipProps={tooltipProps}
                        expectedLabel={expectedLabelPlays}
                        percentageLabel={percentageLabelPlays}
                        inScheduleLabel={inScheduleLabel}
                        outScheduleLabel={outScheduleLabel}
                        unbookedLabel={unbookedLabel}
                        timeFrame={timeframe}
                        type={reportType}
                        field={field}
                        metric={PCAReportMetric.Plays}
                        countFields={isDsp ? [] : countFields}
                        hidePercentage={isDsp}
                    />
                );
            },
        [
            countFields,
            expectedLabelPlays,
            inScheduleLabel,
            isDsp,
            outScheduleLabel,
            percentageLabelPlays,
            timeframe,
            unbookedLabel,
        ],
    );

    const getSummaryWidgetTitle = useCallback(
        (widget: SummaryWidgetConfig) => {
            if (widget.title) return widget.title;

            const metricTitle = metricTitles[widget.metric] ?? widget.metric;

            if (
                widget.kind === 'line' &&
                widget.groupBy.length === 1 &&
                widget.groupBy[0] === 'date'
            ) {
                return `${summaryTitleRunningTotal} ${metricTitle}`;
            }

            if (widget.kind === 'map') {
                return `${metricTitle} ${summaryTitleHeatmap}`;
            }

            const groupByLabels = widget.groupBy.map(groupBy =>
                t(
                    SUMMARY_WIDGET_GROUP_BY_TRANSLATION_KEYS[groupBy] ??
                        (`Modules.Main.Campaigns.Overview.Reports.fields.${groupBy}` as TranslationPath),
                ),
            );

            return `${metricTitle} ${summaryTitleBy} ${groupByLabels.join(summaryTitleJoin)}`;
        },
        [
            metricTitles,
            summaryTitleBy,
            summaryTitleHeatmap,
            summaryTitleJoin,
            summaryTitleRunningTotal,
            t,
        ],
    );

    const getWidgetReportField = useCallback((widget: SummaryWidgetConfig) => {
        if (widget.groupBy.length === 1) {
            return SUMMARY_WIDGET_GROUP_BY_PCA_FIELD_MAP[widget.groupBy[0]] ?? null;
        }

        if (
            widget.groupBy.length === 2 &&
            widget.groupBy[0] === 'display_owner' &&
            widget.groupBy[1] === 'display_format'
        ) {
            return EsPCAReportField.DisplayOwnerFormat as unknown as PCAReportField;
        }

        return null;
    }, []);

    const getWidgetParts = useCallback(
        (widget: SummaryWidgetConfig) => {
            if (widget.kind === 'line') {
                if (showDefaultDspExpectedLine(widget)) {
                    return [
                        {
                            dataKey: expectedLabelForImpactsCharts,
                            labelOverride: expectedLabelImpacts,
                            filterRole: 'expected',
                            comparisonSeries: true,
                            lineProps: {
                                stroke: theme.palette.grey['500'],
                                strokeDasharray: '5 5',
                            },
                            barProps: { fill: theme.palette.grey['500'] },
                        },
                        {
                            dataKey: actualLabelImpacts,
                            labelOverride: actualDeliveredLabelImpacts,
                            filterRole: 'delivered',
                            comparisonSeries: false,
                            lineProps: { stroke: theme.palette.primary.main },
                            barProps: { fill: theme.palette.primary.main },
                        },
                    ] as ChartReportPart[];
                }

                return widget.metric === 'plays' ? dailyPlayParts : dailyImpactParts;
            }

            if (widget.kind === 'bar') {
                if (showDefaultDspExpectedBar(widget)) {
                    return [
                        {
                            dataKey: expectedLabelForImpactsCharts,
                            labelOverride: expectedLabelImpacts,
                            filterRole: 'expected',
                            comparisonSeries: true,
                            lineProps: {
                                stroke: theme.palette.grey['500'],
                                strokeDasharray: '5 5',
                            },
                            barProps: { fill: theme.palette.grey['500'] },
                        },
                        {
                            dataKey: actualLabelImpacts,
                            labelOverride: actualDeliveredLabelImpacts,
                            filterRole: 'delivered',
                            comparisonSeries: false,
                            lineProps: { stroke: theme.palette.primary.main },
                            barProps: { fill: theme.palette.primary.main },
                        },
                    ] as ChartReportPart[];
                }

                const labels = getMetricChartLabels(widget.metric);
                return generateParts({
                    actual: labels.actualLabel,
                    actualLabelOverride: labels.actualLabelOverride,
                    expected: labels.expectedLabel,
                });
            }

            return [] as ChartReportPart[];
        },
        [
            actualDeliveredLabelImpacts,
            actualLabelImpacts,
            dailyImpactParts,
            dailyPlayParts,
            expectedLabelForImpactsCharts,
            expectedLabelImpacts,
            generateParts,
            getMetricChartLabels,
            showDefaultDspExpectedBar,
            showDefaultDspExpectedLine,
            theme.palette.grey,
            theme.palette.primary.main,
        ],
    );

    const getWidgetTooltip = useCallback(
        (widget: SummaryWidgetConfig) => {
            const field = getWidgetReportField(widget);
            if (widget.kind === 'line') {
                return widget.metric === 'plays'
                    ? playsTooltipLabel(FieldReportType.OverTime, null)
                    : impactsTooltipLabel(
                          FieldReportType.OverTime,
                          null,
                          showDefaultDspExpectedLine(widget)
                              ? expectedLabelImpacts
                              : expectedLabelForImpactsCharts,
                      );
            }

            if (widget.kind === 'bar') {
                return widget.metric === 'plays'
                    ? playsTooltipLabel(FieldReportType.Overall, field)
                    : impactsTooltipLabel(FieldReportType.Overall, field);
            }

            return undefined;
        },
        [
            expectedLabelForImpactsCharts,
            expectedLabelImpacts,
            getWidgetReportField,
            impactsTooltipLabel,
            playsTooltipLabel,
            showDefaultDspExpectedLine,
        ],
    );

    const getWidgetXAxisLabels = useCallback(
        (widget: SummaryWidgetConfig) => {
            if (
                widget.kind === 'line' &&
                widget.groupBy.length === 1 &&
                widget.groupBy[0] === 'date'
            ) {
                return [dateLabelLabel];
            }

            const groupBy = widget.groupBy.length > 0 ? widget.groupBy : ['date'];

            return groupBy.map(value =>
                t(
                    SUMMARY_WIDGET_GROUP_BY_TRANSLATION_KEYS[value] ??
                        (`Modules.Main.Campaigns.Overview.Reports.fields.${value}` as TranslationPath),
                ),
            );
        },
        [dateLabelLabel, t],
    );

    const getWidgetFilterOptionLabels = useCallback(
        (widget: SummaryWidgetConfig) => {
            if (widget.metric !== 'impacts') return undefined;

            if (showDefaultDspExpectedLine(widget)) {
                return {
                    expected: expectedLabelImpacts,
                    delivered: actualDeliveredLabelImpacts,
                    both: 'Both',
                };
            }

            if (showDefaultDspExpectedBar(widget)) {
                return {
                    expected: expectedLabelImpacts,
                    delivered: actualDeliveredLabelImpacts,
                    both: 'Both',
                };
            }

            if (
                isDsp &&
                (isGeopathOnly || isAverageAudienceMode) &&
                selectedAudienceLabelForCharts
            ) {
                return {
                    expected: 'Total',
                    delivered: selectedAudienceLabelForCharts,
                    both: 'Both',
                };
            }

            return undefined;
        },
        [
            actualDeliveredLabelImpacts,
            expectedLabelImpacts,
            isAverageAudienceMode,
            isDsp,
            isGeopathOnly,
            selectedAudienceLabelForCharts,
            showDefaultDspExpectedBar,
            showDefaultDspExpectedLine,
        ],
    );

    const buildWidgetReport = useCallback(
        (widget: SummaryWidgetConfig): SummaryWidgetDefinition => {
            const isDateBarWidget =
                widget.kind === 'bar' &&
                widget.groupBy.length === 1 &&
                widget.groupBy[0] === 'date';

            if (widget.source === 'groupByField' && !isDateBarWidget) {
                const currentWindow = latestSummaryWindow.current;
                const reportType = widget.kind === 'line' ? ReportType.Line : ReportType.Bar;
                const chartParts = getWidgetParts(widget);
                const tooltipRenderer = getWidgetTooltip(widget);
                const xAxisLabels = getWidgetXAxisLabels(widget);
                const standardExportDataTransform = (rows: any[]) =>
                    buildStandardSummaryExportData(rows, xAxisLabels, dateLabelLabel);
                const widgetStartDate = currentWindow?.start;
                const widgetEndDate = currentWindow?.end;
                const widgetTimeframe = currentWindow?.timeframe;
                const fetchWidgetRows = async (
                    start: Date,
                    end: Date,
                    timeFrame: ReportDateTimeframe,
                ) => fetchExtraWidgetData(widget, start, end, timeFrame);
                const widgetRequestKey = JSON.stringify({
                    widgetId: widget.id,
                    metric: widget.metric,
                    groupBy: widget.groupBy,
                    source,
                    environmentMode,
                    fieldFilters,
                    targetSeriesKey,
                });

                return {
                    id: widget.id,
                    report: {
                        type: ReportType.Custom,
                        namePrefix: `${id}_${widget.id}`,
                        RenderReport: GroupByFieldWidgetReport,
                        widgetId: widget.id,
                        reportType,
                        startDate: widgetStartDate,
                        endDate: widgetEndDate,
                        timeframe: widgetTimeframe,
                        xAxisDataKey: dateLabelLabel,
                        parts: chartParts,
                        requestKey: widgetRequestKey,
                        renderTooltipLabel: tooltipRenderer,
                        defaultShowNumbers: isDsp,
                        allowPercentToggle: showDefaultDspTargetPercent(widget),
                        percentOfTotal: showDefaultDspTargetPercent(widget),
                        percentOfTotalLabel: shareOfTotalLabel,
                        percentLabelDecimals,
                        filterOptionLabels: getWidgetFilterOptionLabels(widget),
                        exportDataTransform: isDsp
                            ? rows => buildSummaryExportData(rows, widget.metric, xAxisLabels)
                            : standardExportDataTransform,
                        fetchData: fetchWidgetRows,
                    },
                };
            }

            const fallbackWidgetStartDate = latestSummaryWindow.current?.start ?? initialStartDate;
            const fallbackWidgetEndDate = latestSummaryWindow.current?.end ?? initialEndDate;
            const xAxisLabels = getWidgetXAxisLabels(widget);
            const widgetParts = getWidgetParts(widget);
            const standardExportDataTransform = (rows: any[]) =>
                buildStandardSummaryExportData(rows, xAxisLabels, dateLabelLabel);

            if (widget.kind === 'map') {
                return {
                    id: widget.id,
                    report: {
                        type: ReportType.Custom,
                        namePrefix: `${id}_${widget.id}`,
                        RenderReport: MapWidgetReport,
                        widgetId: widget.id,
                        campaignId: id,
                        data: getWidgetData(widget) as any,
                        mapDataType: widget.mapDataType as any,
                        mapDisplayMode: widget.mapDisplayMode as any,
                        loadingState: summaryLoadingState,
                    },
                };
            }

            return {
                id: widget.id,
                report: {
                    type: ReportType.Custom,
                    namePrefix: `${id}_${widget.id}`,
                    RenderReport: StaticChartWidgetReport,
                    widgetId: widget.id,
                    reportType: widget.kind === 'line' ? ReportType.Line : ReportType.Bar,
                    startDate: fallbackWidgetStartDate,
                    endDate: fallbackWidgetEndDate,
                    xAxisDataKey: dateLabelLabel,
                    data: getWidgetData(widget) as any[],
                    parts: widgetParts,
                    loadingState: summaryLoadingState,
                    renderTooltipLabel: getWidgetTooltip(widget),
                    defaultShowNumbers: isDsp,
                    allowPercentToggle: showDefaultDspTargetPercent(widget),
                    percentOfTotal: showDefaultDspTargetPercent(widget),
                    percentOfTotalLabel: shareOfTotalLabel,
                    percentLabelDecimals,
                    filterOptionLabels: getWidgetFilterOptionLabels(widget),
                    exportDataTransform: isDsp
                        ? rows => buildSummaryExportData(rows, widget.metric, xAxisLabels)
                        : standardExportDataTransform,
                },
            };
        },
        [
            buildSummaryExportData,
            dateLabelLabel,
            environmentMode,
            fetchExtraWidgetData,
            fieldFilters,
            getWidgetData,
            getWidgetFilterOptionLabels,
            getWidgetParts,
            getWidgetTooltip,
            getWidgetXAxisLabels,
            id,
            initialEndDate,
            initialStartDate,
            isDsp,
            latestSummaryWindow,
            percentLabelDecimals,
            shareOfTotalLabel,
            showDefaultDspTargetPercent,
            source,
            summaryLoadingState,
            targetSeriesKey,
        ],
    );

    return useMemo<VisibleSummaryWidgetDefinition[]>(
        () =>
            visibleWidgetConfigs.map(widget => ({
                ...buildWidgetReport(widget),
                title: getSummaryWidgetTitle(widget),
                isHeatmap: widget.kind === 'map',
            })),
        [buildWidgetReport, getSummaryWidgetTitle, visibleWidgetConfigs],
    );
};
